import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { fmtDateIt } from "@/lib/services";
import { Users2, CheckCheck, ChevronDown, ChevronRight, Phone, Lock } from "lucide-react";

// Registro giornaliero Presenze/Assenti lato Lavoratore (condiviso per data+appalto+servizio+contesto).
export function AttendanceDayRegister({ open, onClose, ctx, date, serviceLabel }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    if (!ctx || !date) return;
    setLoading(true);
    try {
      const q = new URLSearchParams({ service: ctx.service, context_type: ctx.type, date });
      if (ctx.context_id) q.set("context_id", ctx.context_id);
      if (ctx.appalto_id) q.set("appalto_id", ctx.appalto_id);
      const r = await api.get(`/attendance/day-register?${q.toString()}`);
      setData(r.data);
    } catch (e) { toast.error(formatApiError(e)); onClose(); }
    finally { setLoading(false); }
  }, [ctx, date, onClose]);

  useEffect(() => { if (open) { setExpanded(null); setData(null); load(); } }, [open, load]);

  const body = () => ({ service: ctx.service, context_type: ctx.type, context_id: ctx.context_id || null, appalto_id: ctx.appalto_id || null, date });

  const toggle = async (p) => {
    if (!data?.editable || busy) return;
    const next = p.status === "P" ? "A" : "P";
    setBusy(true);
    try { const r = await api.post("/attendance/day-register/set", { ...body(), participant_id: p.id, status: next }); setData(r.data); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const markAll = async () => {
    if (!data?.editable || busy) return;
    setBusy(true);
    try { const r = await api.post("/attendance/day-register/mark-all-present", body()); setData(r.data); toast.success("Tutti segnati presenti"); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const c = data?.counts || { present: 0, absent: 0, pending: 0, expected: 0 };
  const editable = !!data?.editable;

  if (!date) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto p-0" data-testid="attendance-day-register">
        <DialogHeader className="px-4 pt-4 pb-2 border-b border-slate-100 text-left">
          <DialogTitle className="flex items-center gap-2 text-base"><Users2 className="h-5 w-5 text-primary" /> Presenze utenti</DialogTitle>
          <p className="text-xs text-slate-500 capitalize">{fmtDateIt(date)}</p>
          <p className="text-xs text-slate-500">{serviceLabel}{ctx?.label ? ` · ${ctx.label}` : ""}</p>
          {data && (
            <p className="text-xs font-semibold mt-1" data-testid="attendance-counts">
              <span className="text-emerald-600">{c.present} presenti</span> · <span className="text-red-600">{c.absent} assenti</span> · <span className="text-slate-500">{c.pending} da compilare</span>
            </p>
          )}
          {!editable && data && (
            <span className="inline-flex items-center gap-1 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5 mt-1 w-fit" data-testid="attendance-readonly">
              <Lock className="h-3 w-3" /> Sola lettura (mese chiuso)
            </span>
          )}
        </DialogHeader>

        <div className="px-4 py-3">
          {loading ? (
            <p className="text-sm text-slate-400 text-center py-6">Caricamento…</p>
          ) : !data || data.participants.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-6" data-testid="attendance-no-users">Nessun utente previsto per questa data.</p>
          ) : (
            <>
              {editable && (
                <Button type="button" size="sm" variant="outline" onClick={markAll} disabled={busy}
                  className="w-full mb-3 rounded-lg border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="attendance-mark-all">
                  <CheckCheck className="h-4 w-4 mr-1.5" /> Segna tutti presenti
                </Button>
              )}
              <div className="space-y-1.5">
                {data.participants.map((p) => (
                  <div key={p.id} className="rounded-lg border border-slate-100 bg-white" data-testid={`attendance-row-${p.id}`}>
                    <div className="flex items-center gap-2 p-2">
                      <button type="button" onClick={() => setExpanded(expanded === p.id ? null : p.id)}
                        className="flex items-center gap-1.5 min-w-0 flex-1 text-left" data-testid={`attendance-name-${p.id}`}>
                        {expanded === p.id ? <ChevronDown className="h-4 w-4 text-slate-400 shrink-0" /> : <ChevronRight className="h-4 w-4 text-slate-400 shrink-0" />}
                        <span className="font-medium text-slate-800 text-sm truncate">{p.nome} {p.cognome}</span>
                      </button>
                      <button type="button" onClick={() => toggle(p)} disabled={!editable || busy}
                        data-testid={`attendance-pa-${p.id}`}
                        className={`h-10 w-12 rounded-lg font-bold text-base shrink-0 border-2 transition-colors ${
                          p.status === "P" ? "bg-emerald-500 border-emerald-600 text-white"
                          : p.status === "A" ? "bg-red-500 border-red-600 text-white"
                          : "bg-slate-50 border-slate-200 text-slate-400"} ${!editable ? "opacity-70 cursor-default" : "active:scale-95"}`}>
                        {p.status || "–"}
                      </button>
                    </div>
                    {expanded === p.id && (
                      <div className="px-3 pb-2.5 pt-0.5 border-t border-slate-50 text-xs text-slate-600" data-testid={`attendance-detail-${p.id}`}>
                        <p className="font-medium text-slate-800">{p.nome} {p.cognome}</p>
                        {p.telefono ? (
                          <a href={`tel:${p.telefono}`} className="inline-flex items-center gap-1.5 text-primary mt-1" data-testid={`attendance-tel-link-${p.id}`}>
                            <Phone className="h-3.5 w-3.5" /> {p.telefono}
                          </a>
                        ) : <p className="text-slate-400 mt-1">Telefono non disponibile</p>}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {editable && (
                <Button type="button" onClick={() => { toast.success("Presenze inviate · potrai modificarle entro il mese"); onClose(); }}
                  data-testid="attendance-submit"
                  className="w-full mt-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-semibold">
                  Invia presenze
                </Button>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default AttendanceDayRegister;
